import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BRAND = '#214140';
const GREEN = '#1F8051';
const TEAL  = '#8BBAB8';
const LIGHT = '#DFEAF0';

const StatPill = ({ num, label }) => (
  <div className="text-center px-6 py-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.15)', minWidth: 100 }}>
    <div className="text-3xl font-bold text-white">{num}</div>
    <div className="text-xs uppercase tracking-wide mt-1" style={{ color: TEAL }}>{label}</div>
  </div>
);

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm" style={{ border: `1px solid ${LIGHT}` }}>
    <div className="text-2xl mb-3">{icon}</div>
    <h3 className="font-bold text-lg mb-2" style={{ color: BRAND }}>{title}</h3>
    <p className="text-sm text-gray-600">{desc}</p>
  </div>
);

export default function DistributorsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="py-20 px-4" style={{ background: BRAND }}>
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-block px-4 py-1 rounded-full text-xs font-semibold uppercase tracking-widest mb-6"
            style={{ background: 'rgba(139,186,184,0.2)', color: TEAL }}>
            Distributor Program
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            Bring Cordflex® to<br />Your Territory
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-10">
            Join a growing network of medical device distributors bringing the ICU's most-requested line management solution to hospitals across the country.
          </p>
          <div className="flex justify-center gap-4 flex-wrap mb-12">
            <StatPill num="203" label="Product Requests" />
            <StatPill num="30" label="States" />
            <StatPill num="68%" label="Cite tracing errors" />
            <StatPill num="7" label="Role types" />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/distributors/apply')}
              className="px-8 py-4 rounded-xl font-semibold text-base transition-all hover:opacity-90"
              style={{ background: GREEN, color: 'white' }}>
              Apply to Become a Distributor
            </button>
            <button
              onClick={() => navigate('/distributors/login')}
              className="px-8 py-4 rounded-xl font-semibold text-base transition-all"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
              Partner Sign In
            </button>
          </div>
        </div>
      </section>

      {/* The Opportunity */}
      <section className="py-16 px-4" style={{ background: LIGHT }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3" style={{ color: BRAND }}>The Market Opportunity</h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Cordflex solves a problem every ICU nurse deals with daily — and demand is already proven.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard icon="🏥" title="Massive Unmet Need"
              desc="The US has 5,000+ ICUs with 100,000+ ICU beds, plus step-down, med-surg and other units across 900,000+ staffed hospital beds (AHA). Few have a standardized line management solution. Cordflex is purpose-built for this gap." />
            <FeatureCard icon="📊" title="Proven Demand"
              desc="203 product requests from 30 states, each with a completed survey, before a full sales force is in place. These are warm inbound leads." />
            <FeatureCard icon="🔄" title="Recurring Revenue"
              desc="Cordflex Clips are a consumable product used daily. Once adopted in a unit, reorder demand is consistent and predictable." />
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3" style={{ color: BRAND }}>What Partners Receive</h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            We make it easy for your reps to sell. Everything they need is ready on day one.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon="🎥" title="Training Recordings"
              desc="Access recorded onboarding sessions and real sales calls so your reps can get up to speed fast — on their own schedule." />
            <FeatureCard icon="📋" title="Sales Playbook"
              desc="A complete playbook with talking points, objection handling, clinical evidence, and a quick-start guide for new reps." />
            <FeatureCard icon="📦" title="Sample Program"
              desc="Request trial units for your accounts. We support structured trials with evaluation forms and outcome tracking." />
            <FeatureCard icon="📄" title="Marketing Materials"
              desc="Sell sheets, clinical evidence summaries, and product literature — all ready to use with your accounts." />
            <FeatureCard icon="💬" title="Real-Time Support"
              desc="Direct access to the NTM team via WhatsApp for quick questions during active deals or trials." />
            <FeatureCard icon="🤝" title="Exclusive Territory"
              desc="We work with a limited number of distribution partners per region to protect your investment and avoid channel conflict." />
          </div>
        </div>
      </section>

      {/* Ideal Partner */}
      <section className="py-16 px-4" style={{ background: LIGHT }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3" style={{ color: BRAND }}>Who We're Looking For</h2>
          <p className="text-center text-gray-600 mb-10">
            The best Cordflex distributors have existing relationships with ICU nurses, managers, and supply chain in acute care settings.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {[
              'Active relationships with ICU or critical care purchasing',
              'Experienced reps who sell to bedside clinical staff',
              'Established presence in 1+ hospital systems',
              'Familiar with consumable device sales cycles',
              'Motivated to add a high-demand differentiated product',
              'Aligned with a consultative, evidence-based selling approach'
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-4" style={{ border: `1px solid ${TEAL}30` }}>
                <span style={{ color: GREEN }} className="font-bold mt-0.5">✓</span>
                <span className="text-sm text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 text-center" style={{ background: BRAND }}>
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Learn More?</h2>
          <p className="text-gray-300 mb-8">
            Apply below. We'll review your application and schedule a conversation to see if the fit is right.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/distributors/apply')}
              className="px-8 py-4 rounded-xl font-semibold transition-all hover:opacity-90"
              style={{ background: GREEN, color: 'white' }}>
              Apply Now
            </button>
            <button
              onClick={() => navigate('/distributors/login')}
              className="px-8 py-4 rounded-xl font-semibold transition-all"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)' }}>
              Already a Partner? Sign In
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
